// src/pages/Register.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import config from "../config/env";
import { ReactComponent as Cam } from "../assets/images/cam.svg";
import { ReactComponent as Trash } from "../assets/images/trash.svg";
import { ReactComponent as Logo } from "../assets/images/logo.svg";

const API_KEY = "darwintec-solutions-X-prompt-jai";

export default function Register() {
  const nav = useNavigate();

  const provinceOptions = [
    { value: "", label: "เลือกจังหวัด" },
    { value: "10", label: "สระบุรี" },
    { value: "29", label: "อุดรธานี" },
  ];

  const [fullName, setFullName] = useState("");
  const [lastName, setLastName] = useState("");
  const [gender, setGender] = useState(""); // UI: ชาย/หญิง/ไม่ระบุ
  const [phone, setPhone] = useState("");
  const [provinceId, setProvinceId] = useState(""); // ✅ province_id
  const [amphureId, setAmphureId] = useState(""); // ✅ amphure_id (แทน hubId)

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  // รถที่เข้ารับ (หลายคัน)
  const [plates, setPlates] = useState([""]);

  // รูปบัตร (ยังไม่ส่ง API)
  const [cardFile, setCardFile] = useState(null);
  const [cardPreview, setCardPreview] = useState("");

  // dropdown data from API
  const [provinceLoading, setProvinceLoading] = useState(true);
  const [provinces, setProvinces] = useState([]); // [{id,name_th,...}]
  const [username, setUsername] = useState("");

  const [amphureLoading, setAmphureLoading] = useState(false);
  const [amphures, setAmphures] = useState([]); // [{id,name_th,...}]

  const [submitting, setSubmitting] = useState(false);
  const [errMsg, setErrMsg] = useState("");
  const [successOpen, setSuccessOpen] = useState(false);

  const fileInputRef = useRef(null);

  // line_id จาก localStorage (ของเดิม)
  const line_id = localStorage.getItem("line_uuid") || "";

  // preview image
  useEffect(() => {
    if (!cardFile) {
      setCardPreview("");
      return;
    }
    const url = URL.createObjectURL(cardFile);
    setCardPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [cardFile]);

  // ✅ Load provinces on mount
  useEffect(() => {
    let alive = true;

    (async () => {
      setProvinceLoading(true);
      setErrMsg("");
      try {
        const res = await axios.get(`${config.api || ""}/api/location/provinces`, {
          headers: { "X-API-KEY": API_KEY },
          withCredentials: false,
        });

        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (!alive) return;
        setProvinces(list);
      } catch (err) {
        console.error("GET /api/location/provinces error:", err);
        if (!alive) return;
        setErrMsg("โหลดรายการจังหวัดไม่สำเร็จ");
        setProvinces([]);
      } finally {
        if (alive) setProvinceLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  // ✅ เมื่อเลือกจังหวัด -> โหลด amphures + reset amphureId
  // ✅ เมื่อเลือกจังหวัด -> โหลด hub ทั้งหมด + reset hubId
  useEffect(() => {
    let alive = true;

    (async () => {
      // reset hub when province changes
      setAmphureId("");
      setAmphures([]);

      if (!provinceId) return;

      setAmphureLoading(true);
      setErrMsg("");
      try {
        const provinceLabel =
          provinceOptions.find((x) => x.value === String(provinceId))?.label || "";

        const res = await axios.get(
          `${config.api || ""}/api/location/getAllHub`,
          {
            headers: { "X-API-KEY": API_KEY },
            withCredentials: false,
            params: {
              province: provinceLabel,
            },
          }
        );

        // response ใหม่: { success: true, data: [...] }
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        if (!alive) return;

        // (optional) กรองที่ไม่ถูกลบ
        const filtered = list.filter((x) => !x?.is_delete);

        setAmphures(filtered);
      } catch (err) {
        console.error("GET /api/location/getAllHub error:", err);
        if (!alive) return;
        setErrMsg("โหลดรายการเขต/hub ไม่สำเร็จ");
        setAmphures([]);
      } finally {
        if (alive) setAmphureLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, [provinceId]);

  const canSubmit = useMemo(() => {
    const hasPlate = plates.some((p) => String(p || "").trim().length > 0);
    const pwOk =
      password.length >= 1 &&
      confirmPassword.length >= 1 &&
      password === confirmPassword;

    const phoneDigits = String(phone || "").replace(/[^\d]/g, "");

    return (
      fullName.trim() &&
      lastName.trim() &&
      username.trim() &&
      gender &&
      phoneDigits.length === 10 &&
      provinceId &&
      amphureId &&
      pwOk &&
      hasPlate
    );
  }, [
    fullName,
    lastName,
    username,
    gender,
    phone,
    provinceId,
    amphureId,
    password,
    confirmPassword,
    plates,
  ]);

  function addPlate() {
    setPlates((prev) => [...prev, ""]);
  }

  function setPlate(i, v) {
    setPlates((prev) => prev.map((x, idx) => (idx === i ? v : x)));
  }

  function removePlate(i) {
    setPlates((prev) => {
      const next = prev.filter((_, idx) => idx !== i);
      return next.length === 0 ? [""] : next;
    });
  }

  function mapGender(uiValue) {
    if (uiValue === "ชาย") return "male";
    if (uiValue === "หญิง") return "female";
    if (uiValue === "ไม่ระบุ") return "unknown";
    return "unknown";
  }

  async function onSubmitRegister(e) {
    e?.preventDefault?.();
    if (submitting || !canSubmit) return;

    setSubmitting(true);
    setErrMsg("");

    try {
      const phoneDigits = String(phone || "").replace(/[^\d]/g, "").slice(0, 10);
      if (phoneDigits.length !== 10) {
        setErrMsg("เบอร์โทรต้องมี 10 หลักเท่านั้น");
        return;
      }

      const cleanPlates = plates
        .map((p) => String(p || "").trim())
        .filter(Boolean);

      // ✅ multipart/form-data
      const fd = new FormData();
      fd.append("first_name", String(fullName).trim());
      fd.append("last_name", String(lastName).trim());
      fd.append("phone_number", phoneDigits);
      fd.append("username", String(username).trim());
      fd.append("password", String(password));
      fd.append("gender", mapGender(gender));

      // ✅ ส่งเป็น "ข้อความ JSON" ตาม curl
      fd.append(
        "address",
        JSON.stringify({
          hub_id: Number(amphureId),      // ใช้ amphureId เป็น hub_id
          province_id: Number(provinceId),
        })
      );

      fd.append(
        "vehicles",
        JSON.stringify(cleanPlates.map((plate) => ({ license_plate: plate })))
      );

      // ✅ ส่งไฟล์จริง
      if (cardFile) {
        fd.append("photo", cardFile);
      }

      await axios.post(`${config.api || ""}/api/auth/register`, fd, {
        headers: { "X-API-KEY": API_KEY },
        withCredentials: false,
        // ❌ ไม่ต้อง set Content-Type เอง ให้ axios ใส่ boundary ให้
      });

      setSuccessOpen(true);
      setTimeout(() => nav("/login", { replace: true }), 1500);
    } catch (err) {
      console.error("POST /api/auth/register error:", err);
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "สมัครสมาชิกไม่สำเร็จ";
      setErrMsg(String(msg));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-white sm:w-full md:max-w-[520px] mx-auto">
      {/* top bar */}
      <div className="w-full bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center justify-between">
          <Logo className="h-16 w-auto text-white" />
          <div className="w-10" />
        </div>
      </div>

      <SuccessModal
        open={successOpen}
        onClose={() => {
          setSuccessOpen(false);
          nav("/login", { replace: true }); // ✅ ปิด modal แล้วเด้งไป login
        }}
      />

      <form onSubmit={onSubmitRegister} className="px-4 pt-4 pb-28">
        <div className="text-[20px] font-extrabold text-[#3F775F]">
          ลงทะเบียนใช้งาน
        </div>

        {/* row: ชื่อ / นามสกุล */}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Field label="ชื่อ" required>
            <input
              className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-4 text-[14px] outline-none"
              placeholder="กรอกชื่อ"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
            />
          </Field>
          <Field label="นามสกุล" required>
            <input
              className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-4 text-[14px] outline-none"
              placeholder="กรอกนามสกุล"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </Field>
        </div>

        {/* เพศ */}
        <div className="mt-3">
          <Field label="เพศ" required>
            <Select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              options={[
                { value: "", label: "เลือกเพศ" },
                { value: "ชาย", label: "ชาย" },
                { value: "หญิง", label: "หญิง" },
                { value: "ไม่ระบุ", label: "ไม่ระบุ" },
              ]}
            />
          </Field>
        </div>

        {/* เบอร์ติดต่อ */}
        <div className="mt-3">
          <Field label="หมายเลขโทรศัพท์" required>
            <input
              className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-4 text-[14px] outline-none"
              placeholder="กรอกหมายเลขโทรศัพท์"
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/[^\d]/g, "").slice(0, 10))}
            />
          </Field>
        </div>

        {/* จังหวัด (from API) */}
        <div className="mt-3">
          <Field label="จังหวัด" required>
            <Select
              value={provinceId}
              onChange={(e) => setProvinceId(e.target.value)}
              loading={provinceLoading}
              // disabled={provinceLoading}
              options={provinceOptions}
            // options={[
            //   { value: "", label: provinceLoading ? "กำลังโหลดจังหวัด..." : "เลือกจังหวัด" },
            //   ...provinces.map((p) => ({
            //     value: String(p.id),
            //     label: p.name_th || p.name_en || `จังหวัด #${p.id}`,
            //   })),
            // ]}
            />
          </Field>
        </div>

        {/* อำเภอ (from provinceId) */}
        <div className="mt-3">
          <Field label="เขต/hub" required>
            <Select
              value={amphureId}
              onChange={(e) => setAmphureId(e.target.value)}
              loading={amphureLoading}
              disabled={!provinceId || amphureLoading}
              options={[
                {
                  value: "",
                  label: !provinceId
                    ? "กรุณาเลือกจังหวัดก่อน"
                    : amphureLoading
                      ? "กำลังโหลดเขต/hub..."
                      : "เลือกเขต/hub",
                },
                ...amphures.map((a) => ({
                  value: String(a.id),
                  label: a.name || `Hub #${a.name}`,
                })),
              ]}
            />
          </Field>
        </div>

        <div className="mt-3">
          <Field label="username" required>
            <input
              className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-4 text-[14px] outline-none"
              placeholder="กรอกชื่อผู้ใช้"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
          </Field>
        </div>

        {/* password */}
        <div className="mt-3">
          <Field label="ตั้งรหัสผ่าน" required>
            <input
              className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-4 text-[14px] outline-none"
              placeholder="ตั้งรหัสผ่าน"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        </div>

        {/* confirm password */}
        <div className="mt-3">
          <Field label="กรอกรหัสผ่านอีกครั้ง" required>
            <input
              className="w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-4 text-[14px] outline-none"
              placeholder="กรอกรหัสผ่านอีกครั้ง"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-4 h-[1px] bg-[#E5E7EB]" />

        {/* เพิ่มทะเบียนรถ */}
        <div className="mt-4 flex items-center justify-between">
          <div className="text-[14px] font-semibold text-[#4B5563]">
            กดเพิ่มถ้ามีรถมากกว่า1คัน
          </div>
          <button
            type="button"
            onClick={addPlate}
            className="h-9 px-5 rounded-lg bg-[#ED9C50] text-white text-[14px] font-bold"
          >
            เพิ่ม
          </button>
        </div>

        {/* ทะเบียนรถที่เข้ารับ */}
        <div className="mt-3">
          <Field label="ทะเบียนรถที่เข้ารับ" required>
            <div className="space-y-3">
              {plates.map((p, i) => (
                <div key={i} className="flex items-center gap-3">
                  <input
                    className="flex-1 h-11 rounded-xl border border-[#9CA3AF] bg-white px-4 text-[14px] outline-none"
                    placeholder="กรอกทะเบียนรถที่เข้ารับ"
                    value={p}
                    onChange={(e) => setPlate(i, e.target.value)}
                  />
                  {plates.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removePlate(i)}
                      className="h-11 w-11 rounded-xl bg-[#F7E3E3] grid place-items-center"
                      aria-label="ลบทะเบียนรถ"
                    >
                      <Trash />
                    </button>
                  )}
                </div>
              ))}
            </div>
          </Field>
        </div>

        <div className="mt-4 h-[1px] bg-[#E5E7EB]" />

        {/* รูปบัตร */}
        <div className="mt-4">
          <Field label="แนบรูปถ่าย (สำหรับติดบัตร)" required>
            <div className="rounded-2xl border border-[#9CA3AF] bg-white p-4">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setCardFile(f);
                }}
              />

              <div className="flex items-center justify-center">
                <div className="relative w-[140px] h-[160px] rounded-xl overflow-hidden grid place-items-center">
                  {cardPreview ? (
                    <>
                      <img
                        src={cardPreview}
                        alt="preview"
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setCardFile(null);
                          if (fileInputRef.current) fileInputRef.current.value = "";
                        }}
                        className="absolute top-2 right-2 h-6 w-6 rounded-full bg-white/90 grid place-items-center text-[#EF4444] font-bold"
                        aria-label="ลบรูป"
                      >
                        ×
                      </button>
                    </>
                  ) : (
                    <div className="text-center text-[#4B5563]">
                      <Cam />
                    </div>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full h-10 text-[#4B5563] font-medium"
              >
                อัปโหลดรูป
              </button>
            </div>
          </Field>
        </div>

        {errMsg && (
          <div className="mt-4 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-[13px] text-rose-700">
            {errMsg}
          </div>
        )}

        <div className="h-16" />

        {/* bottom fixed */}
        <div className="fixed bottom-0 left-0 right-0 bg-white">
          <div className="md:max-w-[520px] mx-auto px-4 py-4 border-t border-[#9CA3AF] rounded-t-3xl">
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              className={[
                "w-full h-12 rounded-xl font-bold text-[16px]",
                canSubmit && !submitting
                  ? "bg-[#3F775F] text-white"
                  : "bg-slate-200 text-slate-500 cursor-not-allowed",
              ].join(" ")}
            >
              {submitting ? "กำลังลงทะเบียน..." : "ลงทะเบียน"}
            </button>

            <div className="mt-3 text-center text-[13px] text-[#6B7280]">
              มีบัญชีอยู่แล้ว?{" "}
              <Link to="/login" className="font-bold text-[#3F775F] hover:underline">
                เข้าสู่ระบบ
              </Link>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

/* ---------- UI helpers ---------- */

function Field({ label, required, children }) {
  return (
    <div>
      <div className="text-[14px] font-semibold text-[#4B5563]">
        {label} {required && <span className="text-red-500">*</span>}
      </div>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function SuccessModal({ open, title = "ลงทะเบียนสำเร็จ", desc = "ระบบสร้างบัญชีเรียบร้อยแล้ว", onClose }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[999] bg-black/40 backdrop-blur-[1px] grid place-items-center px-4"
      onMouseDown={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="ลงทะเบียนสำเร็จ"
    >
      <div
        className="w-full max-w-[520px] rounded-2xl bg-white p-6 shadow-xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="mx-auto h-14 w-14 rounded-full bg-emerald-50 grid place-items-center">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
              d="M20 6 9 17l-5-5"
              stroke="#059669"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </div>

        <div className="mt-4 text-center text-[18px] font-semibold text-slate-900">
          {title}
        </div>
        <div className="mt-2 text-center text-[14px] text-slate-600">
          {desc}
        </div>

        <div className="mt-6 flex justify-center">
          <button
            type="button"
            onClick={onClose}
            className="h-11 w-[160px] rounded-xl bg-[#3F775F] text-white font-medium"
          >
            ตกลง
          </button>
        </div>
      </div>
    </div>
  );
}


function Select({ value, onChange, options, disabled, loading }) {
  return (
    <div className="relative">
      <select
        className={[
          "w-full h-11 rounded-xl border border-[#9CA3AF] bg-white px-4 pr-10 text-[14px] outline-none appearance-none",
          disabled ? "opacity-60 cursor-not-allowed" : "",
        ].join(" ")}
        value={value}
        onChange={onChange}
        disabled={disabled}
      >
        {options.map((x) => (
          <option key={x.value} value={x.value} disabled={x.value === ""}>
            {x.label}
          </option>
        ))}
      </select>

      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-[#4B5563]">
        {loading ? "…" : "▼"}
      </span>
    </div>
  );
}
